// Libraries
import { FC, memo } from "react";
import { Document, Page, Text, View, PDFViewer } from "@react-pdf/renderer";

// Components

// Styles
import { styles } from "./PDFLayout.styles";

// Types
import { PDFLayoutProps } from "./PDFLayout.types";

const locale: Intl.LocalesArgument = "en-US";
const dateOptions: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
};

export const PDFLayout: FC<PDFLayoutProps> = memo(
  ({
    showDate = true,
    showPageNumber = true,
    orientation = "portrait",
    watermark,
    projectName,
    title,
    companyName,
    address,
    phone,
    email,
    contact,
    children,
  }) => {
    const date = new Date();
    const formattedDate = date.toLocaleDateString(locale, dateOptions);

    return (
      <PDFViewer
        className={`h-[420px] download-iframe lg:h-full ${
          orientation === "portrait" ? "lg:w-[470px]" : "lg:w-[800px]"
        } max-lg:w-full`}
        showToolbar={false}
      >
        <Document
          style={styles.document}
          creator="RivetAI"
          producer="RivetAI"
          data-testid="pdf-layout"
        >
          <Page
            size="A4"
            orientation={orientation}
            style={styles.page}
            wrap
            data-testid="pdf-layout-page"
          >
            {showDate && (
              <Text style={styles.date} fixed data-testid="pdf-layout-date">
                {formattedDate}
              </Text>
            )}
            <View wrap>
              {!!projectName && (
                <Text
                  style={{
                    marginBottom: title ? 4 : 12,
                    fontSize: 18,
                    fontWeight: "heavy",
                  }}
                >
                  {projectName}
                </Text>
              )}
              {!!title && (
                <Text
                  style={{
                    marginTop: projectName ? 0 : 12,
                    marginBottom: 0,
                    fontSize: 14,
                    fontWeight: "ultrabold",
                  }}
                >
                  {title}
                </Text>
              )}
              {(!!companyName || !!address || !!phone || !!email || !!contact) && (
                <View style={{ marginTop: 12 }}>
                  {!!companyName && (
                    <View style={styles.contactDetails} fixed data-testid="pdf-layout-company-name">
                      <Text style={{ fontWeight: "semibold" }}>Company: </Text>
                      <Text style={{ fontWeight: "normal" }}>{companyName}</Text>
                    </View>
                  )}
                  {!!address && (
                    <View style={styles.contactDetails} fixed data-testid="pdf-layout-address">
                      <Text style={{ fontWeight: "semibold" }}>Address: </Text>
                      <Text style={{ fontWeight: "normal" }}>{address}</Text>
                    </View>
                  )}
                  {!!phone && (
                    <View style={styles.contactDetails} fixed data-testid="pdf-layout-phone">
                      <Text style={{ fontWeight: "semibold" }}>Phone: </Text>
                      <Text style={{ fontWeight: "normal" }}>{phone}</Text>
                    </View>
                  )}
                  {!!email && (
                    <View style={styles.contactDetails} fixed data-testid="pdf-layout-email">
                      <Text style={{ fontWeight: "semibold" }}>Email: </Text>
                      <Text style={{ fontWeight: "normal" }}>{email}</Text>
                    </View>
                  )}
                  {!!contact && (
                    <View style={styles.contactDetails} fixed data-testid="pdf-layout-contact">
                      <Text style={{ fontWeight: "semibold" }}>Contact: </Text>
                      <Text style={{ fontWeight: "normal" }}>{contact}</Text>
                    </View>
                  )}
                </View>
              )}
              {children}
            </View>
            <Text style={styles.builtBy} fixed data-testid="pdf-layout-built-by">
              Powered by RivetAI
            </Text>
            {showPageNumber && (
              <Text
                style={styles.pageNumber}
                render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
                fixed
                data-testid="pdf-layout-page-number"
              />
            )}
            {watermark && (
              <Text style={styles.watermark} fixed data-testid="pdf-layout-watermark">
                {watermark}
              </Text>
            )}
          </Page>
        </Document>
      </PDFViewer>
    );
  }
);

export default PDFLayout;
